/**
 * BookingFormDialog (N3, Oct 2026) — standalone create and DRAFT-only
 * edit dialog for the Logistics Bookings screen. Unlike
 * BookVehicleDialog (source-triggered, pre-linked), this creates an
 * unlinked DRAFT booking; linking happens later from the detail
 * dialog. Edit mode is only reachable for DRAFT bookings (backend
 * enforces it too — PUT on any other status is 409).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import PermButton from "./PermButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  vehicle_type: "",
  from_location: "",
  to_location: "",
  appointment_date: "",
  delivery_location: "",
  geo_lat: "",
  geo_long: "",
  transporter_name: "",
  transporter_phone: "",
  booking_cost: "",
};

// Required on every booking (server enforces too).
const REQUIRED = { vehicle_type: "Vehicle type", from_location: "From location", to_location: "To location" };

export default function BookingFormDialog({ open, onClose, booking, onSaved, branches = [] }) {
  const editing = Boolean(booking);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (booking) {
      setForm({
        ...EMPTY_FORM,
        vehicle_type: booking.vehicle_type || "",
        from_location: booking.from_location || "",
        to_location: booking.to_location || "",
        appointment_date: booking.appointment_date || "",
        delivery_location: booking.delivery_location || "",
        geo_lat: booking.geotag?.lat ?? "",
        geo_long: booking.geotag?.long ?? "",
        transporter_name: booking.transporter_name || "",
        transporter_phone: booking.transporter_phone || "",
        booking_cost: booking.booking_cost ?? "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, booking]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSave = async () => {
    const missing = Object.keys(REQUIRED).filter((k) => !String(form[k] || "").trim());
    if (missing.length) {
      return toast.error(`Required: ${missing.map((k) => REQUIRED[k]).join(", ")}`);
    }
    setSaving(true);
    try {
      const payload = {
        vehicle_type: form.vehicle_type || null,
        from_location: form.from_location || null,
        to_location: form.to_location || null,
        appointment_date: form.appointment_date || null,
        delivery_location: form.delivery_location || null,
        geotag:
          form.geo_lat !== "" && form.geo_long !== ""
            ? { lat: Number(form.geo_lat), long: Number(form.geo_long) }
            : null,
        transporter_name: form.transporter_name || null,
        transporter_phone: form.transporter_phone || null,
        booking_cost: form.booking_cost !== "" ? Number(form.booking_cost) : null,
      };
      const res = editing
        ? await axios.put(`${API}/logistics-bookings/${booking.id}`, payload)
        : await axios.post(`${API}/logistics-bookings`, payload);
      toast.success(
        editing
          ? `Booking ${res.data.booking_code} updated`
          : `Draft booking ${res.data.booking_code} created`
      );
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Failed to save booking");
    } finally {
      setSaving(false);
    }
  };

  const req = (key) => REQUIRED[key] && <span className="text-rose-600">*</span>;
  const textField = (key, label, type = "text") => (
    <div key={key}>
      <Label>{label} {req(key)}</Label>
      <Input
        type={type}
        value={form[key]}
        onChange={set(key)}
        data-testid={`bf-field-${key}`}
      />
    </div>
  );
  // Keep a legacy free-text origin selectable when editing an old DRAFT.
  const branchOptions = form.from_location && !branches.includes(form.from_location)
    ? [form.from_location, ...branches] : branches;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="booking-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${booking.booking_code}` : "New Booking"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Fields are editable only while the booking is in DRAFT."
              : "Creates a standalone DRAFT booking. Link it to a source later from the booking detail."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm max-h-[55vh] overflow-y-auto pr-1">
          <div>
            <Label>Vehicle type (ft) {req("vehicle_type")}</Label>
            <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
              <SelectTrigger data-testid="bf-field-vehicle_type">
                <SelectValue placeholder="32 / 20 / 14" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="32">32 ft</SelectItem>
                <SelectItem value="20">20 ft</SelectItem>
                <SelectItem value="14">14 ft</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From location {req("from_location")}</Label>
            <Select value={form.from_location} onValueChange={set("from_location")}>
              <SelectTrigger data-testid="bf-field-from_location">
                <SelectValue placeholder="Select branch" />
              </SelectTrigger>
              <SelectContent>
                {branchOptions.map((b) => (
                  <SelectItem key={b} value={b} data-testid={`bf-from-option-${b}`}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {textField("to_location", "To location")}
          {textField("delivery_location", "Delivery location")}
          {textField("appointment_date", "Appointment date", "date")}
          {textField("transporter_name", "Transporter name")}
          {textField("transporter_phone", "Transporter phone")}
          <div>
            <Label>Booking Cost (₹)</Label>
            <Input
              type="number"
              value={form.booking_cost}
              onChange={set("booking_cost")}
              onWheel={(e) => e.currentTarget.blur()}
              className="no-spinner"
              data-testid="bf-field-booking_cost"
            />
          </div>
          <div className="col-span-2">
            <Label>Geotag (lat, long)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" step="any" placeholder="Latitude"
                value={form.geo_lat} onChange={set("geo_lat")} data-testid="bf-field-geo_lat" />
              <Input type="number" step="any" placeholder="Longitude"
                value={form.geo_long} onChange={set("geo_long")} data-testid="bf-field-geo_long" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <PermButton entity="LogisticsBooking" action="CREATE" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </PermButton>
          <PermButton
            entity="LogisticsBooking"
            action={editing ? "UPDATE" : "CREATE"}
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="booking-form-save-btn"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create draft"}
          </PermButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
